import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => (Number(v) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const dateFr = (d) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('fr-FR') : '—');
const heureFr = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : '—');

function Ligne({ label, value, testid, mono }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`text-sm font-semibold text-slate-900 ${mono ? 'font-mono break-all text-xs sm:text-right' : ''}`} data-testid={testid}>{value}</span>
    </div>
  );
}

export default function VerifierRecapContributions() {
  const { recapId } = useParams();
  const [etat, setEtat] = useState({ loading: true });

  useEffect(() => {
    axios.get(`${API}/api/recus-ess/recap-contributions/verifier/${recapId}`)
      .then(({ data }) => setEtat({ data }))
      .catch(() => setEtat({ erreur: true }));
  }, [recapId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10" data-testid="verifier-recap-page">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="bg-[#1e3a5f] text-white px-6 py-5 flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-amber-300" />
          <div>
            <h1 className="text-lg font-bold">Vérification de document officiel</h1>
            <p className="text-xs text-blue-100">Récapitulatif des contributions solidaires encaissées</p>
          </div>
        </div>
        <div className="px-6 py-6">
          {etat.loading && <div className="flex items-center gap-2 text-slate-500" data-testid="verifier-recap-loading"><Loader2 className="h-4 w-4 animate-spin" /> Vérification en cours…</div>}
          {etat.erreur && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200" data-testid="verifier-recap-invalide">
              <XCircle className="h-6 w-6 text-red-600 shrink-0" />
              <div><p className="font-semibold text-red-800">Document inconnu</p>
                <p className="text-sm text-red-700 mt-1">Ce récapitulatif n'a pas été émis par OSO – OBJECTIF SCOP OUTREMER ou son identifiant est incorrect.</p></div>
            </div>
          )}
          {etat.data && (
            <>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-5" data-testid="verifier-recap-authentique">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                <div><p className="font-semibold text-emerald-800">Document authentique</p>
                  <p className="text-sm text-emerald-700 mt-1">Émis et signé par {etat.data.emetteur}.</p></div>
              </div>
              <Ligne label="N° de document" value={etat.data.numero} testid="verifier-recap-numero" />
              <Ligne label="Période" value={`du ${dateFr(etat.data.debut)} au ${dateFr(etat.data.fin)}`} testid="verifier-recap-periode" />
              <Ligne label="Nombre de reçus ESS" value={etat.data.nb_recus} testid="verifier-recap-nb" />
              <Ligne label="Total encaissé" value={eur(etat.data.total)} testid="verifier-recap-total" />
              <Ligne label="Édité le" value={heureFr(etat.data.edite_le)} testid="verifier-recap-date" />
              <Ligne label="Empreinte numérique (SHA-256)" value={etat.data.empreinte_sha256} testid="verifier-recap-empreinte" mono />
              <p className="text-xs text-slate-400 mt-5">Comparez ces informations avec le PDF reçu. Toute différence indique un document modifié.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
